import { InMemoryWebStorage, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { getConfig } from "../config";

// Two route trees, one bundle (WEB-01): `/` signs in with the `customers` realm, `/ops` with the `ops` realm.
// Each realm has its own UserManager, so an ops token never reaches a customer route and the other way round.
export type Realm = "customers" | "ops";

export const REALM_ROOT: Record<Realm, string> = { customers: "", ops: "/ops" };

export function callbackPath(realm: Realm): string {
  return `${REALM_ROOT[realm]}/auth/callback`;
}

export function signInPath(realm: Realm): string {
  return `${REALM_ROOT[realm]}/sign-in`;
}

export function authority(realm: Realm): string {
  return `${getConfig().oidcBase}/${realm}`;
}

/** The Keycloak account console, where a user enrols a passkey or changes their password (WEB-F-SH-01). */
export function accountConsoleUrl(realm: Realm): string {
  return `${authority(realm)}/account`;
}

const managers = new Map<Realm, UserManager>();

export function userManager(realm: Realm): UserManager {
  let m = managers.get(realm);
  if (!m) {
    const origin = window.location.origin;
    m = new UserManager({
      authority: authority(realm),
      client_id: getConfig().clientId,
      redirect_uri: origin + callbackPath(realm),
      post_logout_redirect_uri: origin + signInPath(realm),
      response_type: "code",
      scope: "openid profile email",
      // Tokens stay in memory only (AGENTS.md, security). The PKCE verifier and state of a sign-in in flight
      // stay in sessionStorage, the library's default, because they must survive the redirect to Keycloak.
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
      // Renewal uses the refresh token (Keycloak rotates it); no hidden iframe, so no third-party cookies.
      automaticSilentRenew: true,
      monitorSession: false,
      loadUserInfo: false,
    });
    managers.set(realm, m);
  }
  return m;
}

export function resetUserManagersForTests(): void {
  managers.clear();
}

// Tokens are lost when the page reloads. A non-secret marker in sessionStorage says this tab was signed in, so the
// app goes straight back through Keycloak (which still has its session cookie) instead of showing the sign-in page.
const markerKey = (realm: Realm) => `cv.signedIn.${realm}`;

export function wasSignedIn(realm: Realm): boolean {
  try {
    return sessionStorage.getItem(markerKey(realm)) === "1";
  } catch {
    return false;
  }
}

/** A tab goes back through Keycloak by itself after a failed renewal at most once in this long (#5). */
export const RESUME_GAP_MS = 5 * 60_000;
const resumedKey = (realm: Realm) => `cv.resumedAt.${realm}`;

/**
 * Whether a tab whose renewal failed may go back through Keycloak by itself now, and if so notes that it did: not
 * again within RESUME_GAP_MS, so a Keycloak that answers but whose tokens keep failing cannot loop.
 */
export function mayResume(realm: Realm, now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(resumedKey(realm)) ?? 0);
    if (now - last < RESUME_GAP_MS) return false;
    sessionStorage.setItem(resumedKey(realm), String(now));
    return true;
  } catch {
    return false;
  }
}

export function setSignedInMarker(realm: Realm, on: boolean): void {
  try {
    if (on) sessionStorage.setItem(markerKey(realm), "1");
    else sessionStorage.removeItem(markerKey(realm));
  } catch {
    // Storage blocked: the user sees the sign-in page after a reload, nothing else changes.
  }
}

/** Only same-app paths are accepted as a place to return to after sign-in. */
export function safeReturnTo(value: unknown, realm: Realm): string {
  const root = REALM_ROOT[realm] || "/";
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return root;
  if (realm === "customers" && (value === "/ops" || value.startsWith("/ops/"))) return root;
  if (realm === "ops" && !(value === "/ops" || value.startsWith("/ops/"))) return root;
  return value;
}
